import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { Button, MOTION_ANIMATION_SPEED_MD, Select, useModal } from '@dashboard/ui'
import { WebsiteConfig, WebsiteRead, WebsiteTemplateRead } from '@/api/dashboard'
import { EditWebsiteTemplateModal } from '@/hub-editor/dialogs/EditWebsiteTemplateModal'
import { useWebsiteTemplates } from '@/hub-editor/api/useWebsiteTemplates'
import { useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { dashboardClient } from '@/api'
import { ThemeEditor } from '@/hub-editor/widgets/ThemeEditor'
import { fetchUploadTheme } from '@/hub-editor'
import { Theme, themeQueryOpts } from 'src/layouts/token-studio'
import { FieldGroup, ToastSeverity, useToast } from '@/ui'

export const ColorSchemeEditor = ({
  website,
  updateTheme,
  onChange,
  handleTokensPage,
}: {
  website: WebsiteRead
  updateTheme: (css: string) => void
  onChange: (value: WebsiteConfig) => void
  handleTokensPage: () => void
}) => {
  const ql = useQueryClient()
  const { companyId, gameId } = useParams() as { companyId: string; gameId: string }
  const { t } = useTranslation()
  const showToast = useToast()
  const { data: templates = [], refetch } = useWebsiteTemplates(companyId, gameId)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)

  const { reset, handleSubmit } = useForm<WebsiteTemplateRead>()

  const openEditModal = useModal<{ item: WebsiteTemplateRead }>(props => (
    <EditWebsiteTemplateModal
      {...props}
      template={props.item}
      onUpdated={async template => {
        await refetch()
        setSelectedTemplateId(template.id)
      }}
      onRemoved={() => {
        refetch()
        setSelectedTemplateId(null)
        reset({})
        onChange({})
      }}
    />
  ))

  const selectedTemplate = useMemo(
    () => templates.find(it => it.id === selectedTemplateId),
    [selectedTemplateId, templates],
  )

  const mergedWebsite = useMemo(() => {
    return {
      ...website,
      config: {
        ...website.config,
        ...selectedTemplate?.config,
      },
    }
  }, [website, selectedTemplate])

  const onSaveClick = async (data: WebsiteTemplateRead) => {
    const segmentedThemeQueryOpts = selectedTemplateId
      ? themeQueryOpts(gameId, website, selectedTemplateId)
      : themeQueryOpts(gameId, website)

    const themeRaw = ql.getQueryData<Theme>(segmentedThemeQueryOpts.queryKey)

    if (themeRaw) {
      const { get_url } = await fetchUploadTheme(
        new File([JSON.stringify(themeRaw)], 'theme.json', { type: 'application/json' }),
      )

      await dashboardClient.v1.updateWebsiteTemplate(companyId, gameId, data.id, {
        config: { ...data.config, theme_raw_url: get_url },
      })
    } else {
      await dashboardClient.v1.updateWebsiteTemplate(companyId, gameId, data.id, {
        config: data.config,
      })
    }

    showToast({ message: t('saved'), severity: ToastSeverity.success })
    refetch()
  }

  useEffect(() => {
    const currentTemplate = templates.find(it => it.id === selectedTemplateId)
    if (!currentTemplate) {
      return
    }

    reset(currentTemplate)

    onChange({
      ...currentTemplate.config,
    })
  }, [selectedTemplateId])

  return (
    <div className="grow">
      <div className="border-b border-border-secondary p-2 text-caption-xs font-medium uppercase text-text-tertiary">
        {t('website-template.store-color')}
      </div>

      <div className="flex flex-col gap-1.5 p-2">
        <FieldGroup label={t('website-template.color-theme')} style={{ marginBottom: 0 }}>
          <Select
            items={templates.map(item => ({
              children: item.name,
              value: item.id,
            }))}
            placeholder={t('website-template.choose-theme')}
            value={selectedTemplateId}
            onChange={v => setSelectedTemplateId(v as string)}
            renderMenuFooter={state => (
              <Button
                className="w-full"
                variant="outline"
                size="sm"
                onClick={() => {
                  state.setIsOpen(false)
                  setTimeout(() => {
                    openEditModal({
                      item: {
                        config: {},
                      } as WebsiteTemplateRead,
                    })
                  }, MOTION_ANIMATION_SPEED_MD * 500)
                }}
              >
                {t('website-template.add')}
              </Button>
            )}
          />
        </FieldGroup>

        {selectedTemplateId && (
          <>
            <Button
              variant="outline"
              color="secondary"
              className="w-full text-text-brand-primary"
              onClick={() => openEditModal({ item: templates.find(it => it.id == selectedTemplateId)! })}
            >
              {t('website-template.rules')}
            </Button>
            <Button onClick={handleSubmit(onSaveClick)} className="w-full" variant="outline">
              {t('apply')}
            </Button>
          </>
        )}
      </div>

      {selectedTemplateId && (
        <ThemeEditor
          key={selectedTemplateId}
          segmentTemplateId={selectedTemplateId}
          website={mergedWebsite}
          updateTheme={updateTheme}
          findToken={() => void 0}
          handleTokensPage={handleTokensPage}
        />
      )}
    </div>
  )
}
